import {
  buildCondition,
  type Combinator,
  type Condition,
  type Variable,
  type WizardParameter,
  type WizardRule,
} from "./clearanceRulesConfig";

export function duplicateParameterNames(parameters: WizardParameter[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const param of parameters) {
    if (param.name === "") continue;
    if (seen.has(param.name)) duplicates.add(param.name);
    seen.add(param.name);
  }
  return duplicates;
}

function conditionChildren(condition: Condition, combinator: Combinator): Condition[] | null {
  if (combinator === "all") return "all" in condition ? condition.all : null;
  return "any" in condition ? condition.any : null;
}

// An order-insensitive fingerprint of a built condition with every "$NAME"
// resolved to its parameter's value - nested groups of the same combinator
// are flattened and repeated children deduplicated, so two conditions that
// select exactly the same points under the pipeline's semantics compare equal.
function canonicalCondition(condition: Condition, parameterValues: Map<string, number>): string {
  for (const combinator of ["all", "any"] as const) {
    const children = conditionChildren(condition, combinator);
    if (!children) continue;
    const flatten = (nodes: Condition[]): Condition[] =>
      nodes.flatMap((child) => {
        const nested = conditionChildren(child, combinator);
        return nested ? flatten(nested) : [child];
      });
    const canonicalChildren = [
      ...new Set(flatten(children).map((child) => canonicalCondition(child, parameterValues))),
    ].sort();
    return canonicalChildren.length === 1
      ? canonicalChildren[0]
      : `${combinator}(${canonicalChildren.join(",")})`;
  }
  const leaf = condition as Extract<Condition, { name: Variable }>;
  const resolvedValue =
    typeof leaf.value === "string" ? parameterValues.get(leaf.value.slice(1)) ?? leaf.value : leaf.value;
  return JSON.stringify([leaf.name, leaf.operator, resolvedValue]);
}

function parameterValuesByName(parameters: WizardParameter[]): Map<string, number> {
  return new Map(
    parameters.filter((param) => param.name !== "").map((param) => [param.name, param.value])
  );
}

// Keys of every rule whose fingerprint matches at least one other rule's -
// all of them, not just the later ones, since either could be the one the
// admin meant to change. A rule fingerprinting to null is never reported.
function ruleKeysSharingFingerprint(
  rules: WizardRule[],
  fingerprint: (rule: WizardRule) => string | null
): Set<string> {
  const ruleKeysByFingerprint = new Map<string, string[]>();
  for (const rule of rules) {
    const ruleFingerprint = fingerprint(rule);
    if (ruleFingerprint === null) continue;
    ruleKeysByFingerprint.set(ruleFingerprint, [
      ...(ruleKeysByFingerprint.get(ruleFingerprint) ?? []),
      rule.key,
    ]);
  }
  return new Set([...ruleKeysByFingerprint.values()].filter((keys) => keys.length > 1).flat());
}

export function duplicateConditionRuleKeys(
  rules: WizardRule[],
  parameters: WizardParameter[]
): Set<string> {
  const parameterValues = parameterValuesByName(parameters);
  return ruleKeysSharingFingerprint(rules, (rule) => {
    const conditions = buildCondition(rule.root);
    return conditions === null ? null : canonicalCondition(conditions, parameterValues);
  });
}

// Rules setting exactly the same severity on exactly the same axes, with
// "$NAME" references resolved - an action still missing its severity is
// skipped, same as buildClearanceConfig drops it.
export function duplicateActionRuleKeys(
  rules: WizardRule[],
  parameters: WizardParameter[]
): Set<string> {
  const parameterValues = parameterValuesByName(parameters);
  return ruleKeysSharingFingerprint(rules, (rule) => {
    const severities = [...rule.actions]
      .map(([axisName, value]) => {
        const resolved =
          value.kind === "param" ? parameterValues.get(value.name) ?? `$${value.name}` : value.value;
        return resolved === null ? null : JSON.stringify([axisName, resolved]);
      })
      .filter((entry): entry is string => entry !== null)
      .sort();
    return severities.length === 0 ? null : severities.join(",");
  });
}
